import React from 'react';
import { Link } from 'react-router-dom';
import { AcademicCapIcon, CalendarIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

/**
 * UniversityCard - Enhanced application card with visual progress and status
 */
const UniversityCard = ({ application, onClick }) => {
    const getStatusColor = (status) => {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'offer' || normalized === 'accepted') {
            return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        }
        if (normalized === 'submitted' || normalized === 'processing') {
            return 'bg-blue-100 text-blue-700 border-blue-200';
        }
        if (normalized === 'draft') {
            return 'bg-slate-100 text-slate-600 border-slate-200';
        }
        if (normalized === 'rejected') {
            return 'bg-rose-100 text-rose-700 border-rose-200';
        }
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    };

    const getProgressPercentage = (status) => {
        const stages = ['draft', 'submitted', 'processing', 'decision', 'offer'];
        const normalized = (status || 'draft').toLowerCase();
        const idx = stages.indexOf(normalized);
        if (idx < 0) return 20;
        return Math.round(((idx + 1) / stages.length) * 100);
    };

    const getDeadlineInfo = (deadline) => {
        if (!deadline) return null;
        const date = new Date(deadline);
        const now = new Date();
        const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { text: 'Overdue', color: 'text-rose-600 bg-rose-50' };
        }
        if (diffDays <= 3) {
            return { text: `${diffDays}d left`, color: 'text-amber-600 bg-amber-50' };
        }
        if (diffDays <= 7) {
            return { text: `${diffDays}d left`, color: 'text-blue-600 bg-blue-50' };
        }
        return { text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: 'text-slate-500 bg-slate-50' };
    };

    const progress = getProgressPercentage(application.status);
    const deadlineInfo = getDeadlineInfo(application.deadline || application.metadata?.deadline);

    const CardContent = () => (
        <div className={`
            bg-white border border-slate-200 rounded-2xl p-5 
            transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-indigo-200
            group cursor-pointer
        `}>
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-100 transition-colors">
                        <AcademicCapIcon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                            {application.university_name || application.programs?.university?.name || 'University'}
                        </h3>
                        <p className="text-sm text-slate-500 truncate">
                            {application.program_name || application.programs?.name || 'Program'}
                        </p>
                    </div>
                </div>
                <span className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full capitalize border ${getStatusColor(application.status)}`}>
                    {application.status || 'Draft'}
                </span>
            </div>

            {/* Program/Intake info */}
            <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
                {application.intake && (
                    <div className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        <span>{application.intake}</span>
                    </div>
                )}
                {deadlineInfo && (
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${deadlineInfo.color}`}>
                        ⏰ {deadlineInfo.text}
                    </div>
                )}
            </div>

            {/* Progress bar */}
            <div className="mb-4">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Progress</span>
                    <span>{progress}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
            </div>

            {/* Action */}
            <div className="flex items-center justify-between">
                <div className="flex -space-x-1">
                    {/* Placeholder for document icons */}
                    <div className="h-6 w-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] text-slate-500">📄</div>
                    <div className="h-6 w-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] text-slate-500">📋</div>
                </div>
                <span className="text-sm text-indigo-600 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    View details <ArrowRightIcon className="h-4 w-4" />
                </span>
            </div>
        </div>
    );

    if (onClick) {
        return (
            <div onClick={() => onClick(application)}>
                <CardContent />
            </div>
        );
    }

    return (
        <Link to={`/portal/applications/${application.id}`}>
            <CardContent />
        </Link>
    );
};

export default UniversityCard;
